import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";
import { debugCountry, resolveCountryId, setSelectedCountry, getStoredSelectedCountry, normalizeCountryId } from "../utils/country";

export const LocationContext = createContext(null);

const LS_KEY = "incooking_location_v1";

// safe parse
function readLS() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    return obj;
  } catch {
    return null;
  }
}

function writeLS(obj) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(obj));
  } catch { }
}

export function LocationProvider({ children }) {
  // --- location state ---
  const [country, setCountry] = useState(""); // "1" or "2"
  const [countrySource, setCountrySource] = useState("");
  const [usZip, setUsZip] = useState("");
  const [usState, setUsState] = useState("");
  const [storeLocationId, setStoreLocationId] = useState("");
  const [storeLabel, setStoreLabel] = useState("");
  const [guestState, setGuestState] = useState(null);
  const [guestLoaded, setGuestLoaded] = useState(false);
  const guestLoadedRef = useRef(false);

  // για να μην εμφανίζεται gate πριν προλάβουμε να φορτώσουμε
  const [hydrated, setHydrated] = useState(false);

  const applyGuestState = useCallback((data, { respectExplicitSelection = true } = {}) => {
    const payload = data || {};
    setGuestState(payload);

    const loc = payload?.location || {};
    const stored = getStoredSelectedCountry();
    const hasExplicit =
      !!stored.id && (stored.source === "user" || stored.source === "localStorage:user");

    if (respectExplicitSelection && hasExplicit) {
      debugCountry("guest.skip.explicit", { id: stored.id });
      return payload;
    }

    if (loc && loc.country != null) {
      const normalizedCountry = normalizeCountryId(loc.country);
      if (normalizedCountry) {
        setCountry(normalizedCountry);
        setCountrySource("guest");
        debugCountry("guest.apply", { id: normalizedCountry, source: "guest" });
      }
    }
    if (loc && loc.us_zip != null) setUsZip(String(loc.us_zip));
    if (loc && loc.us_state != null) setUsState(String(loc.us_state));
    if (loc && loc.store_location_id != null) setStoreLocationId(String(loc.store_location_id));
    return payload;
  }, []);

  const refreshGuestState = useCallback(
    async ({ respectExplicitSelection = true } = {}) => {
      try {
        const res = await api.guest.getState?.();
        const data = res?.data || res || {};
        return applyGuestState(data, { respectExplicitSelection });
      } catch {
        return null;
      } finally {
        if (!guestLoadedRef.current) {
          setGuestLoaded(true);
          guestLoadedRef.current = true;
        }
      }
    },
    [applyGuestState]
  );

  // 1) Hydrate από localStorage στο πρώτο mount
  useEffect(() => {
    const saved = readLS();

    if (saved) {
      if (saved.country != null) setCountry(String(saved.country));
      if (saved.usZip != null) setUsZip(String(saved.usZip));
      if (saved.usState != null) setUsState(String(saved.usState));
      if (saved.storeLocationId != null) setStoreLocationId(String(saved.storeLocationId));
      if (saved.storeLabel != null) setStoreLabel(String(saved.storeLabel));
      if (saved.countrySource != null) {
        const rawSource = String(saved.countrySource);
        setCountrySource(rawSource === "localStorage:user" ? "user" : rawSource);
      }
    }
    // If no stored country, resolve from explicit selection or default to GR
    const savedCountry = normalizeCountryId(saved?.country);
    if (!savedCountry) {
      const resolved = resolveCountryId({ locationCountry: "", guestCountry: "", geoCountry: "" });
      const normalizedSource = resolved.source === "localStorage:user" ? "user" : resolved.source;
      setCountry(resolved.id);
      setCountrySource(normalizedSource);
      debugCountry("init.resolve", { ...resolved, normalizedSource });
    } else {
      debugCountry("init.saved", { id: String(savedCountry), source: saved?.countrySource || "" });
    }
    setHydrated(true);
    setGuestLoaded(false);
    guestLoadedRef.current = false;
  }, []);

  // 2) Persist σε localStorage σε κάθε αλλαγή (αφού γίνει hydrate)
  useEffect(() => {
    if (!hydrated) return;

    const payload = {
      country: country ? String(country) : "",
      countrySource: countrySource ? String(countrySource) : "",
      usZip: usZip ? String(usZip) : "",
      usState: usState ? String(usState) : "",
      storeLocationId: storeLocationId ? String(storeLocationId) : "",
      storeLabel: storeLabel ? String(storeLabel) : "",
      updatedAt: Date.now(),
    };
    writeLS(payload);
    if (country && countrySource === "user") {
      setSelectedCountry(country, "user");
    }
  }, [country, countrySource, usZip, usState, storeLocationId, storeLabel, hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    debugCountry("context.change", { id: country, source: countrySource });
  }, [country, countrySource, hydrated]);

  // 3) initial guest-state hydration from backend
  useEffect(() => {
    if (!hydrated) return;

    let cancelled = false;

    (async () => {
      await refreshGuestState({ respectExplicitSelection: true });
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [hydrated, refreshGuestState]);

  useEffect(() => {
    if (!process.env.REACT_APP_DEBUG_GUEST_STATE) return;
    if (guestLoaded) return;
    const t = setTimeout(() => {
      if (!guestLoaded) {
        console.warn("[LocationContext] guestLoaded still false after 2s");
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [guestLoaded]);

  const explicitUserSelectedCountry = useMemo(() => {
    const stored = getStoredSelectedCountry();
    return !!(stored.id && (stored.source === "user" || stored.source === "localStorage:user"));
  }, [country, countrySource]);

  const guestCountry = useMemo(() => {
    const loc = guestState?.location;
    const id = normalizeCountryId(loc?.country);
    return id || "";
  }, [guestState]);

  const resolvedCountryId = useMemo(
    () => resolveCountryId({ locationCountry: country, guestCountry }).id,
    [country, guestCountry]
  );

  const value = useMemo(
    () => ({
      // state
      country,
      countrySource,
      usZip,
      usState,
      storeLocationId,
      storeLabel,
      hydrated,
      guestState,
      guestLoaded,
      guestCountry,
      explicitUserSelectedCountry,
      resolvedCountryId,

      // setters
      setCountry: (value, source = "user") => {
        const normalized = normalizeCountryId(value) || "";
        setCountry(normalized);
        setCountrySource(source);
        // optimistic guest-location update so UI re-renders instantly
        if (normalized) {
          setGuestState((prev) => {
            const base = prev && typeof prev === "object" ? prev : {};
            const location = base.location && typeof base.location === "object" ? base.location : {};
            return {
              ...base,
              location: {
                ...location,
                country: normalized,
              },
            };
          });
        }
      },
      setUsZip,
      setUsState,
      setStoreLocationId,
      setStoreLabel,
      refreshGuestState,
    }),
    [
      country,
      countrySource,
      usZip,
      usState,
      storeLocationId,
      storeLabel,
      hydrated,
      guestState,
      guestLoaded,
      guestCountry,
      explicitUserSelectedCountry,
      resolvedCountryId,
      refreshGuestState,
    ]
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useLocationCtx() {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error("useLocationCtx must be used within LocationProvider");
  return ctx;
}
